import React, { memo, useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Tooltip } from '@iris/react-components';

import { Button } from 'reaxl';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck';

import { setupReconditioningModal, triggerReconditioningModal } from '@/containers/vdp/VehicleFeaturesContainer/VehicleFeatureHighlights/utils/reconditioningModalUtils';

/**
 * Tooltip differentiator component for items like "Low Miles", "Well Equipped"
 */
const TooltipDifferentiator = memo(({ differentiator, tooltipContent }) => (
    <Tooltip trigger={(
        <span
            className="text-link text-nowrap link-undecorated"
        >
            {differentiator}
        </span>
    )}
    >
        {tooltipContent}
    </Tooltip>
));

/**
 * Clickable differentiator component for items like "New Brakes", "Detailed", "New Tires"
 */
const ClickableDifferentiator = memo(({ differentiator, onClick }) => {
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    }, [onClick]);

    return (
        <Button
            bsStyle="text"
            className="text-nowrap"
            onClick={onClick}
            onKeyDown={handleKeyDown}
        >
            {differentiator}
        </Button>
    );
});

/**
 * Factory function to determine which differentiator component to render
 */
const DifferentiatorItem = memo(({
    differentiator,
    tooltipDifferentiators,
    popupDifferentiators,
    getTooltipContent,
    handlePopupClick,
}) => {
    if (tooltipDifferentiators.has(differentiator)) {
        return (
            <TooltipDifferentiator
                differentiator={differentiator}
                tooltipContent={getTooltipContent(differentiator)}
            />
        );
    }

    if (popupDifferentiators.has(differentiator)) {
        return (
            <ClickableDifferentiator
                differentiator={differentiator}
                onClick={handlePopupClick}
            />
        );
    }

    return null;
});

// Handler for tooltip content (New Brakes, Detailed, New Tires)
const getTooltipContent = (differentiator) => {
    const tooltipMap = {
        'Low Miles': 'This vehicle has a lower mileage than 50% of similar vehicles',
        'Well Equipped': 'This vehicle is better equipped than 50% of similar vehicles',
    };
    return tooltipMap[differentiator];
};

const tooltipDifferentiators = new Set(['Low Miles', 'Well Equipped']);
const popupDifferentiators = new Set(['New Brakes', 'Detailed', 'New Tires']);

/**
 * Component to display differentiators with proper spacing and interactive behavior
 * @param {string[]} differentiators - Array of differentiator text items
 */
function DifferentiatorsDisplay({ differentiators }) {
    const { fyuseId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    // Set up LS360 reconditioning modal automatically
    useEffect(() => {
        const cleanup = setupReconditioningModal(fyuseId);
        return cleanup;
    }, [fyuseId]);

    // Handler for popup content (New Brakes, Detailed, New Tires)
    const handlePopupClick = useCallback(() => {
        // Trigger the reconditioning modal using our utility
        triggerReconditioningModal();
    }, []);

    if (!differentiators || differentiators.length === 0) {
        return null;
    }

    return (
        <div className="iris:flex iris:items-center">
            {differentiators.map((differentiator, index) => (
                <React.Fragment key={`${differentiator}-${index}`}>
                    <DifferentiatorItem
                        differentiator={differentiator}
                        tooltipDifferentiators={tooltipDifferentiators}
                        popupDifferentiators={popupDifferentiators}
                        getTooltipContent={getTooltipContent}
                        handlePopupClick={handlePopupClick}
                    />
                    {/* Add dot separator if not the last item */}
                    {index < differentiators.length - 1 && (
                        <span className="margin-horizontal-1">•</span>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

DifferentiatorsDisplay.propTypes = {
    differentiators: PropTypes.arrayOf(PropTypes.string),
};

export default DifferentiatorsDisplay;
